import { useEffect, useState } from 'react';
import { useAppState } from '../../app/state';
import { LanguagePicker } from '../../components/LanguagePicker';
import { canAiAdmin, canBackupAdmin, canEnterEdit, canUserPerms } from '../../lib/permissions';
import { helpText, type HelpSection } from '../../i18n/help';
import { registerHandle } from '../../app/handles';

/**
 * The help guide.
 *
 * The first of the eleven modals to move, and the smallest — which makes it the one to
 * establish the pattern on. The plan calls the modals "the single biggest simplification
 * available in the whole port", and this is why: all eleven are mounted simultaneously in the
 * body and shown by toggling a `.show` class, so the app permanently holds the DOM of every
 * screen nobody is looking at. Conditionally mounting them means only the open one exists.
 *
 * The guide is composed by ROLE. Everyone gets `main`; the rest are appended for whoever
 * holds the matching permission, so the page describes the app the reader actually has rather
 * than one with buttons they will never see.
 */

/** Section, and the permission that earns it. `main` is unconditional. */
const SECTIONS: { key: HelpSection; when: (perm: Parameters<typeof canEnterEdit>[0]) => boolean }[] = [
  { key: 'main', when: () => true },
  // Whoever can reach edit mode gets the chapter about it (V0.37: any participant brick).
  { key: 'edit', when: canEnterEdit },
  { key: 'perms', when: canUserPerms },
  { key: 'aiadmin', when: canAiAdmin },
  // V0.48. Behind its OWN permission rather than the admin one, because BACKUP_ADMIN is
  // standalone — a guide that explained restoring to somebody who cannot reach the tab would
  // be the exact failure this list exists to prevent.
  { key: 'backups', when: canBackupAdmin },
];

export interface HelpModalHandle {
  open: () => void;
  close: () => void;
}

export function HelpModal({ host }: { host: HTMLElement }) {
  const s = useAppState();
  const [open, setOpen] = useState(false);

  // The host carries the full-screen layout and the .show rule, so the class lands there —
  // the same arrangement as every region in this phase, while the CSS still lives in the
  // vanilla stylesheet.
  useEffect(() => {
    host.classList.toggle('show', open);
  }, [open, host]);

  useEffect(() => {
    const handle: HelpModalHandle = { open: () => setOpen(true), close: () => setOpen(false) };
    registerHandle('help', handle);
  }, []);

  // Escape closes it, like every other full-screen surface in the app.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (!open) return null;

  const html = SECTIONS.filter((sec) => sec.when(s.perm))
    .map((sec) => helpText(sec.key, s.currentLocale))
    .join('');

  return (
    <>
      <div className="perm-head">
        <h2>
          <svg className="ico">
            <use href="#i-info" />
          </svg>
          InfoStation — help &amp; guide
        </h2>
        <LanguagePicker title="Language" />
        {/* The ids here are part of the contract, not decoration. The rule for this
            migration is that a region is ported when the suite passes UNEDITED — so when
            dropping `help-body` and `help-close` broke two assertions, the right fix was to
            keep them, not to rewrite the test. They cost nothing and they are how the suite
            addresses this modal. */}
        <button className="btn" id="help-close" onClick={() => setOpen(false)}>
          <svg className="ico">
            <use href="#i-x" />
          </svg>
          <span>Back to InfoStation</span>
        </button>
      </div>
      <div className="perm-wrap">
        <div className="perm-inner">
          {/* Authored HTML from i18n/help.ts — see the note there on why this is safe, and
              on what would have to change if anything user-supplied ever fed it. */}
          <div className="cfg-help" id="help-body" dangerouslySetInnerHTML={{ __html: html }} />
        </div>
      </div>
    </>
  );
}

/* The `LanguagePicker` that lived here (uncontrolled, driven by chrome.ts's delegated listener)
   is `components/LanguagePicker.tsx` since V0.53.5 — the one implementation every header
   renders, controlled from the snapshot. */
